// ============================================================================
// saveStatus — the one global autosave status (Design Constitution F02-B, Q02)
// ============================================================================
// Every autosave reports here; the header shows one indicator: 'Saving…' while
// any save runs, 'Saved' for a moment after the last one lands, and
// 'Not Saved — Retry' while any save has failed. Locally a screen shows only a
// failure (beside the row or field), never its own 'Saved' (Q02).
//
// Keys name one save target (a row id, a field), so two quick saves of the same
// field count once and a retry clears that key's failure. reportSaving returns
// the attempt's number: a caller that passes it back is ignored once a newer
// attempt for the same key has started, so an older save that lands late never
// reports Saved over a newer failure (or a failure over a newer success). A plain module store
// read with useSyncExternalStore: no provider, safe to call from any save path.
// ============================================================================
import {useSyncExternalStore} from 'react';

export type SaveState = 'idle' | 'saving' | 'saved' | 'failed';

export type SaveStatusSnapshot = {
  state: SaveState;
  /** Retry every failed save, newest first; null when nothing failed. */
  retry: (() => void) | null;
  failedCount: number;
};

export const SAVED_MS = 2500;

const saving = new Set<string>();
const failed = new Map<string, () => void>();
const latestAttempt = new Map<string, number>();
let attemptSeq = 0;
let savedUntil = 0;
let savedTimer: ReturnType<typeof setTimeout> | null = null;
let snapshot: SaveStatusSnapshot = {state: 'idle', retry: null, failedCount: 0};
const listeners = new Set<() => void>();

function compute(now: number): SaveStatusSnapshot {
  if (failed.size > 0) {
    const retries = [...failed.values()].reverse();
    return {state: 'failed', retry: () => retries.forEach((r) => r()), failedCount: failed.size};
  }
  if (saving.size > 0) return {state: 'saving', retry: null, failedCount: 0};
  if (now < savedUntil) return {state: 'saved', retry: null, failedCount: 0};
  return {state: 'idle', retry: null, failedCount: 0};
}

function publish() {
  const next = compute(Date.now());
  if (next.state === snapshot.state && next.failedCount === snapshot.failedCount && next.state !== 'failed') return;
  snapshot = next;
  listeners.forEach((l) => l());
}

/** A save for `key` started. Returns the attempt number to pass back when it settles. */
export function reportSaving(key: string): number {
  const attempt = ++attemptSeq;
  latestAttempt.set(key, attempt);
  saving.add(key);
  failed.delete(key);
  publish();
  return attempt;
}

/** True while `attempt` is the newest save started for `key` (no attempt given: true). */
export function isLatestSave(key: string, attempt?: number): boolean {
  return attempt === undefined || latestAttempt.get(key) === attempt;
}

/** The save for `key` landed. 'Saved' shows for SAVED_MS once nothing else is saving. */
export function reportSaved(key: string, attempt?: number): void {
  if (!isLatestSave(key, attempt)) return;
  saving.delete(key);
  failed.delete(key);
  savedUntil = Date.now() + SAVED_MS;
  if (savedTimer) clearTimeout(savedTimer);
  savedTimer = setTimeout(publish, SAVED_MS + 10);
  publish();
}

/** The save for `key` failed; `retry` runs it again (and should report again). */
export function reportSaveFailed(key: string, retry: () => void, attempt?: number): void {
  if (!isLatestSave(key, attempt)) return;
  saving.delete(key);
  failed.set(key, retry);
  publish();
}

/** The value that failed to save is no longer wanted (the draft went back to the
 * saved value): drop `key`'s failure, leaving any save still running. */
export function clearSaveFailure(key: string): void {
  if (failed.delete(key)) publish();
}

/** The record or screen that owned `key` went away; forget it. */
export function forgetSave(key: string): void {
  const had = saving.delete(key) || failed.delete(key);
  if (had) publish();
}

export function subscribeSaveStatus(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getSaveStatus(): SaveStatusSnapshot {
  return snapshot;
}

export function useSaveStatus(): SaveStatusSnapshot {
  return useSyncExternalStore(subscribeSaveStatus, getSaveStatus, getSaveStatus);
}

/** AuthContext's older status: the app-store blob saver sets it ('saving', then
 * 'saved' for 2.5s, or 'error' for 4s after its three attempts). */
export type LegacySaveStatus = '' | 'saving' | 'saved' | 'error';

/**
 * One indicator for both sources (F02-B): a failure wins, then saving, then
 * saved. A legacy failure carries no retry hook (the blob saver already tried
 * three times), so it reports failed with retry null and the header shows
 * 'Not Saved' without a button that would do nothing (P01).
 */
export function mergeSaveStatus(
  store: SaveStatusSnapshot,
  legacy: LegacySaveStatus | null | undefined,
): SaveStatusSnapshot {
  if (store.state === 'failed') return store;
  if (legacy === 'error') return {state: 'failed', retry: null, failedCount: 1};
  if (store.state === 'saving' || legacy === 'saving') return {state: 'saving', retry: null, failedCount: 0};
  if (store.state === 'saved' || legacy === 'saved') return {state: 'saved', retry: null, failedCount: 0};
  return store;
}

/** Test seam: clear every key and timer. */
export function resetSaveStatusForTests(): void {
  saving.clear();
  failed.clear();
  latestAttempt.clear();
  savedUntil = 0;
  if (savedTimer) clearTimeout(savedTimer);
  savedTimer = null;
  snapshot = {state: 'idle', retry: null, failedCount: 0};
  listeners.forEach((l) => l());
}
